/**
 * A tiny software rasterizer for the login screen's pixel art: a buffer of packed colours, polygons
 * and lines filled on whole pixels, and an ordered-dither matrix.
 *
 * No canvas and no pixi, so the art can be painted -- and asserted -- in a unit test, and rendered
 * to a PNG outside the browser. The scene hands the finished buffer to a canvas with putImageData.
 */

export type Pt = [number, number]

/**
 * Logical (GUI) pixels per art pixel, for ALL of the loading and login screens' pixel art: the scene,
 * the wordmark, the wheel. One size of pixel everywhere -- nothing is drawn with smaller pixels or
 * scaled; anything that has to be bigger or smaller is redrawn with more or fewer of them.
 */
export const ART_PX = 2

/**
 * A colour packed the way a Uint32Array over ImageData holds it on a little-endian machine (every
 * browser the game ships to): R in the low byte, A in the high one. 0 is fully transparent.
 */
export const rgb = (hex: number, alpha: number = 255): number =>
    (((alpha & 0xff) << 24) | ((hex & 0xff) << 16) | (hex & 0xff00) | ((hex >>> 16) & 0xff)) >>> 0

export const alphaOf = (c: number): number => c >>> 24

/** The colour as 0xRRGGBB, for a pixi tint. */
export const hexOf = (c: number): number => ((c & 0xff) << 16) | (c & 0xff00) | ((c >>> 16) & 0xff)

/** `a` toward `b` by `t`, channel by channel. Alpha comes from `a`. */
export const mix = (a: number, b: number, t: number): number => {
    if (t <= 0) {
        return a
    }
    if (t >= 1) {
        return ((a & 0xff000000) | (b & 0x00ffffff)) >>> 0
    }
    const r = (a & 0xff) + ((b & 0xff) - (a & 0xff)) * t
    const g = ((a >>> 8) & 0xff) + (((b >>> 8) & 0xff) - ((a >>> 8) & 0xff)) * t
    const bl = ((a >>> 16) & 0xff) + (((b >>> 16) & 0xff) - ((a >>> 16) & 0xff)) * t
    return ((a & 0xff000000) | (Math.round(bl) << 16) | (Math.round(g) << 8) | Math.round(r)) >>> 0
}

/** A rectangle of pixels whose top-left pixel is (x0, y0) in whatever space it was made for. */
export class PixelBuffer {
    readonly data: Uint32Array

    constructor(readonly w: number, readonly h: number, readonly x0: number = 0, readonly y0: number = 0) {
        this.data = new Uint32Array(Math.max(1, w * h))
    }

    /** The index of (x, y), or -1 outside the buffer. */
    index = (x: number, y: number): number => {
        const i = x - this.x0
        const j = y - this.y0
        return i < 0 || j < 0 || i >= this.w || j >= this.h ? -1 : j * this.w + i
    }

    get = (x: number, y: number): number => {
        const i = this.index(x, y)
        return i < 0 ? 0 : this.data[i]
    }

    set = (x: number, y: number, c: number) => {
        const i = this.index(x, y)
        if (i >= 0) {
            this.data[i] = c
        }
    }

    /** Lay `c` over what is there at `alpha` (0..1), the way a translucent sprite would. */
    blend = (x: number, y: number, c: number, alpha: number) => {
        const i = this.index(x, y)
        if (i < 0 || alpha <= 0) {
            return
        }
        const under = this.data[i]
        const ua = alphaOf(under) / 255
        if (ua <= 0) {
            this.data[i] = ((Math.round(alpha * 255) << 24) | (c & 0x00ffffff)) >>> 0
            return
        }
        const out = ua + alpha * (1 - ua)
        const mixed = mix(under, c, alpha / out)
        this.data[i] = ((Math.round(out * 255) << 24) | (mixed & 0x00ffffff)) >>> 0
    }

    clear = () => this.data.fill(0)

    /** The smallest buffer holding every painted pixel, in the same coordinates (undefined when empty). */
    cropped = (): PixelBuffer | undefined => {
        let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity
        for (let j = 0; j < this.h; j++) {
            for (let i = 0; i < this.w; i++) {
                if (this.data[j * this.w + i]) {
                    if (i < x0) x0 = i
                    if (i > x1) x1 = i
                    if (j < y0) y0 = j
                    if (j > y1) y1 = j
                }
            }
        }
        if (x1 < 0) {
            return undefined
        }
        const out = new PixelBuffer(x1 - x0 + 1, y1 - y0 + 1, this.x0 + x0, this.y0 + y0)
        for (let j = y0; j <= y1; j++) {
            out.data.set(this.data.subarray(j * this.w + x0, j * this.w + x1 + 1), (j - y0) * out.w)
        }
        return out
    }

    /** Copy every opaque-ish pixel of `src` in, offset by (dx, dy). */
    draw = (src: PixelBuffer, dx: number = 0, dy: number = 0) => {
        for (let j = 0; j < src.h; j++) {
            for (let i = 0; i < src.w; i++) {
                const c = src.data[j * src.w + i]
                if (c) {
                    this.set(src.x0 + i + dx, src.y0 + j + dy, c)
                }
            }
        }
    }

    /**
     * Into an RGBA byte array `w * h * 4` long, mirrored left to right when asked. That is the scene
     * flipping the authored drawing onto the other side of the screen at upload time.
     */
    writeRgba = (out: Uint32Array, mirrored: boolean) => {
        if (!mirrored) {
            out.set(this.data)
            return
        }
        const w = this.w
        for (let j = 0; j < this.h; j++) {
            const row = j * w
            for (let i = 0; i < w; i++) {
                out[row + i] = this.data[row + w - 1 - i]
            }
        }
    }
}

/**
 * Every pixel whose centre lies inside the polygon (even-odd), row by row. A sliver thinner than a
 * pixel can come out empty; callers that must show something plot its centre themselves.
 */
export const fillPolygon = (pts: Pt[], plot: (x: number, y: number) => void) => {
    if (pts.length < 3) {
        return
    }
    let minY = Infinity
    let maxY = -Infinity
    for (const p of pts) {
        if (p[1] < minY) minY = p[1]
        if (p[1] > maxY) maxY = p[1]
    }
    const xs: number[] = []
    for (let y = Math.ceil(minY - 0.5); y <= Math.floor(maxY - 0.5); y++) {
        const yc = y + 0.5
        xs.length = 0
        for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
            const yi = pts[i][1]
            const yj = pts[j][1]
            if (yi > yc !== yj > yc) {
                xs.push(pts[i][0] + ((yc - yi) * (pts[j][0] - pts[i][0])) / (yj - yi))
            }
        }
        xs.sort((a, b) => a - b)
        for (let k = 0; k + 1 < xs.length; k += 2) {
            for (let x = Math.ceil(xs[k] - 0.5); x <= Math.floor(xs[k + 1] - 0.5); x++) {
                plot(x, y)
            }
        }
    }
}

/** A one-pixel line from the pixel holding (x0, y0) to the one holding (x1, y1). */
export const drawLine = (x0: number, y0: number, x1: number, y1: number, plot: (x: number, y: number) => void) => {
    let x = Math.floor(x0)
    let y = Math.floor(y0)
    const xe = Math.floor(x1)
    const ye = Math.floor(y1)
    const dx = Math.abs(xe - x)
    const dy = -Math.abs(ye - y)
    const sx = x < xe ? 1 : -1
    const sy = y < ye ? 1 : -1
    let err = dx + dy
    for (let guard = 0; guard < 4096; guard++) {
        plot(x, y)
        if (x === xe && y === ye) {
            return
        }
        const e2 = 2 * err
        if (e2 >= dy) {
            err += dy
            x += sx
        }
        if (e2 <= dx) {
            err += dx
            y += sy
        }
    }
}

/** A polyline through `pts`, one pixel wide, never plotting the same pixel twice in a row. */
export const drawPolyline = (pts: Pt[], plot: (x: number, y: number) => void) => {
    let lastX = NaN
    let lastY = NaN
    const once = (x: number, y: number) => {
        if (x !== lastX || y !== lastY) {
            lastX = x
            lastY = y
            plot(x, y)
        }
    }
    for (let i = 1; i < pts.length; i++) {
        drawLine(pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1], once)
    }
}

/** The 8x8 Bayer matrix: the classic ordered dither, so a dissolve or a gradient never crawls. */
const BAYER8 = [
    0, 32, 8, 40, 2, 34, 10, 42, 48, 16, 56, 24, 50, 18, 58, 26, 12, 44, 4, 36, 14, 46, 6, 38, 60, 28, 52, 20, 62, 30, 54, 22, 3, 35, 11, 43, 1,
    33, 9, 41, 51, 19, 59, 27, 49, 17, 57, 25, 15, 47, 7, 39, 13, 45, 5, 37, 63, 31, 55, 23, 61, 29, 53, 21,
]

/** The dither threshold at a pixel, in [0, 1). */
export const bayer = (x: number, y: number): number => BAYER8[(y & 7) * 8 + (x & 7)] / 64

/** A stable per-integer hash in [0, 1), for scattering detail without a generator. */
export const hash01 = (n: number): number => {
    let h = Math.imul(n ^ 0x9e3779b9, 0x85ebca6b)
    h ^= h >>> 13
    h = Math.imul(h, 0xc2b2ae35)
    h ^= h >>> 16
    return (h >>> 0) / 4294967296
}

/**
 * Lay keyframe `b` over keyframe `a` by `w` (0..1) onto `out`: where both have a pixel its colour
 * blends, where only one does that pixel fades out or in. All three buffers are the same rectangle.
 *
 * This is how anything rotating or travelling in pixel art moves smoothly here: each keyframe is a
 * crisp raster, and only the change from one to the next is faded.
 */
export const crossFade = (out: PixelBuffer, a: PixelBuffer, b: PixelBuffer, w: number) => {
    const n = out.data.length
    for (let i = 0; i < n; i++) {
        const ca = a.data[i]
        const cb = b.data[i]
        if (!ca && !cb) continue
        const x = out.x0 + (i % out.w)
        const y = out.y0 + Math.floor(i / out.w)
        if (ca && cb) {
            const blended = mix(ca, cb, w)
            const alpha = (alphaOf(ca) + (alphaOf(cb) - alphaOf(ca)) * w) / 255
            if (alpha >= 1) out.data[i] = blended
            else out.blend(x, y, blended, alpha)
        } else if (ca) {
            out.blend(x, y, ca, ((1 - w) * alphaOf(ca)) / 255)
        } else {
            out.blend(x, y, cb, (w * alphaOf(cb)) / 255)
        }
    }
}
